import React from 'react';
import { Lookup, shouldShowResults } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { Conditional } from '@codametrix/ui-components';
import { AmplifyCore } from '@codametrix/ui-common';

type ImprovementChargeProps = {
  improvement: AmplifyAPI.DocumentImprovement;
  improvementsAction: CMxCommonApp.Capability;
  processInstance: AmplifyAPI.ProcessInstance;
  procedureSearchResults: CMxCommonApp.CodeInfo[];
  activeItem: CMxAPI.CaseRun;
  userContext: CMxAPI.OrganizationContext;
  onImprovementUpdate: (
    improvementRequest: AmplifyAPI.ImprovementRequest
  ) => void;
  onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => void;
  onSearchClear: (codeQuery: Accelerate.CodeActionContextual) => void;
};

export const ImprovementCharge: React.FC<ImprovementChargeProps> = (
  props: ImprovementChargeProps
) => {
  const {
    improvement,
    improvementsAction,
    processInstance,
    activeItem,
    userContext,
    procedureSearchResults,
    onCodeSearch,
    onImprovementUpdate,
    onSearchClear
  } = props;

  const handleChargeSearch = (codeQuery: CMxCommonApp.CodeQuery) => {
    const query = {
      ...codeQuery,
      searchType: 'CPT',
      context: {
        activeItem: { ...activeItem },
        userContext: { ...userContext },
        processInstance: { ...processInstance },
        improvementGuid: improvement.document_improvement_guid
      }
    };

    if (codeQuery?.text?.length >= 2) {
      onCodeSearch({ ...query });
    } else {
      onSearchClear({ ...query });
    }
  };

  const handleChargeUpdate = (isPotentialCharge: boolean, codeData: string) => {
    const charge = isPotentialCharge ? 'potential_charge_code' : 'charge_code';
    const improvementRequest: AmplifyAPI.ImprovementRequest = {
      processInstance,
      documentImprovement: {
        document_improvement_guid: improvement.document_improvement_guid,
        [charge]: codeData
      }
    };
    onImprovementUpdate(improvementRequest);
  };

  const handleChargeSelection = (
    searchResult: Accelerate.CodeActionPayload
  ) => {
    if (searchResult?.context?.defaultPrevented) {
      onSearchClear({ context: searchResult.context });
    } else {
      handleChargeUpdate(false, searchResult?.codeInfo?.code);
    }
  };
  const handlePotentialChargeSelection = (
    searchResult: Accelerate.CodeActionPayload
  ) => {
    handleChargeUpdate(true, searchResult?.codeInfo?.code);
  };
  const handleChargeRemove = (ev: React.MouseEvent) => {
    ev.preventDefault();
    handleChargeUpdate(false, null);
  };
  const handlePotentialChargeRemove = (ev: React.MouseEvent) => {
    ev.preventDefault();
    handleChargeUpdate(true, null);
  };
  const lookupRef: React.RefObject<Lookup> = React.createRef();
  const isViewOnly = !AmplifyCore.conditionalUtils.capabilityValue(
    improvementsAction?.on
  );

  /**
   * Do not show the charge row when the case is in view only and
   * both charge code and potential charge code are not present
   */
  const isViewOnlyWithNoCharge =
    isViewOnly &&
    !improvement?.charge_code &&
    !improvement?.potential_charge_code;

  const existingCharge = !isViewOnlyWithNoCharge && (
    <div className="procedure-code">
      <div className="charge-left">
        <span>Charge:</span>
        <span
          className={improvement?.charge_code ? 'procedure-label' : 'no-code'}>
          {improvement?.charge_code ?? '-'}
        </span>
      </div>
      <div className="charge-right">
        <Conditional {...improvementsAction}>
          <div className="charge-remove" onClick={handleChargeRemove}>
            <CMXIcon
              svgIconName="BiomeCancelCircle"
              svgIconClass="improvement-provider-remove"
              svgIconSize="18"
            />
          </div>
        </Conditional>
      </div>
    </div>
  );
  const existingPotentialCharge = !isViewOnlyWithNoCharge && (
    <div className="potential-code">
      <div className="charge-left">
        <span>Potential Charge:</span>
        <span
          className={
            improvement?.potential_charge_code ? 'procedure-label' : 'no-code'
          }>
          {improvement?.potential_charge_code ?? '-'}
        </span>
      </div>
      <div className="charge-right">
        <Conditional {...improvementsAction}>
          <div
            className="potential-charge-remove"
            onClick={handlePotentialChargeRemove}>
            <CMXIcon
              svgIconName="BiomeCancelCircle"
              svgIconClass="improvement-provider-remove"
              svgIconSize="18"
            />
          </div>
        </Conditional>
      </div>
    </div>
  );
  return (
    <div className="charge-row">
      <div className="charge-row-left">
        {improvement?.charge_code || isViewOnly ? (
          existingCharge
        ) : (
          <Conditional {...improvementsAction} displayMode="flex">
            <div className="procedure-code-add">
              <Lookup
                ref={lookupRef}
                actionPrefix=""
                placeholderText="Add charge"
                shouldShowResults={shouldShowResults}
                onSearch={handleChargeSearch}
                onSearchSelection={handleChargeSelection}
                searchResults={procedureSearchResults}
                showSearchButton={false}
                attachResultsToPortal={true}
                resultsPortalExtraClass="improvements-lookup-results-portal"
              />
              <CMXIcon
                svgIconName="BiomeChevronDownSVG20"
                svgIconSize="20"
                svgIconClass="select-checvron"
              />
            </div>
          </Conditional>
        )}
      </div>
      <div className="charge-row-right">
        {improvement?.potential_charge_code || isViewOnly ? (
          existingPotentialCharge
        ) : (
          <Conditional {...improvementsAction} displayMode="flex">
            <div className="potential-code-add">
              <Lookup
                ref={lookupRef}
                actionPrefix=""
                placeholderText="Add potential charge"
                shouldShowResults={shouldShowResults}
                onSearch={handleChargeSearch}
                onSearchSelection={handlePotentialChargeSelection}
                searchResults={procedureSearchResults}
                showSearchButton={false}
                attachResultsToPortal={true}
                resultsPortalExtraClass="improvements-lookup-results-portal"
              />
              <CMXIcon
                svgIconName="BiomeChevronDownSVG20"
                svgIconSize="20"
                svgIconClass="select-checvron"
              />
            </div>
          </Conditional>
        )}
      </div>
    </div>
  );
};

ImprovementCharge.displayName = 'ImprovementCharge';
